import { useEffect, useState } from 'react';

function getAspectLimitedMaxWidth(maxHeight, aspectRatio) {
  const numericValue = Number.parseFloat(maxHeight);
  const unit = maxHeight.replace(String(numericValue), '').trim();

  if (!Number.isFinite(numericValue) || !unit) return '100%';

  return `${numericValue * aspectRatio}${unit}`;
}

function LightboxImage({
  src,
  placeholderSrc,
  alt,
  aspectRatio,
  maxHeight = '80vh',
  frameClassName = '',
  imageClassName = '',
  placeholderClassName = '',
  loadingLabel = 'Loading image',
}) {
  const [imageState, setImageState] = useState({ src, status: 'loading' });
  const status = imageState.src === src ? imageState.status : 'loading';
  const isLoaded = status === 'loaded';
  const hasError = status === 'error';
  const showPlaceholder = Boolean(placeholderSrc && placeholderSrc !== src && !isLoaded);
  const frameStyle = aspectRatio
    ? {
        aspectRatio,
        maxHeight,
        maxWidth: getAspectLimitedMaxWidth(maxHeight, aspectRatio),
        width: '100%',
      }
    : undefined;
  const frameSizingClass = aspectRatio ? 'max-w-full' : 'min-h-[45vh] w-full';
  const imageSizingClass = aspectRatio ? 'h-full w-full object-contain' : '';

  useEffect(() => {
    setImageState({ src, status: 'loading' });
  }, [src]);

  return (
    <div
      className={`relative flex ${frameSizingClass} items-center justify-center overflow-hidden bg-black/25 ${frameClassName}`}
      style={frameStyle}
      aria-busy={!isLoaded && !hasError}
    >
      {showPlaceholder ? (
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 h-full w-full scale-105 object-cover blur-2xl opacity-55 transition-opacity duration-300 ${placeholderClassName}`}
        />
      ) : null}

      {!isLoaded && !hasError ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div
            className="inline-flex items-center gap-3 rounded-full bg-black/60 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-sm"
            role="status"
          >
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white"
              aria-hidden="true"
            />
            <span>{loadingLabel}</span>
          </div>
        </div>
      ) : null}

      {hasError ? (
        <div className="z-10 max-w-sm bg-black/65 px-5 py-4 text-center text-sm font-semibold text-white">
          Image could not be loaded.
        </div>
      ) : null}

      <img
        src={src}
        alt={alt}
        decoding="async"
        onLoad={() => setImageState({ src, status: 'loaded' })}
        onError={() => setImageState({ src, status: 'error' })}
        className={`relative z-0 ${imageSizingClass} transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        } ${hasError ? 'hidden' : ''} ${imageClassName}`}
      />
    </div>
  );
}

export default LightboxImage;
